import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GATEWAY_STATUS } from '../constants/gateway';
import { formatHeartbeatAge } from '../utils/gatewayStatus';

/**
 * Compact gateway status: green/red/grey dot + label, optional heartbeat age.
 * Designed to sit under the Server name in the project header.
 */
const GatewayStatusBanner = ({
  status,
  secondsSinceHeartbeat,
  loading,
  available,
}) => {
  const theme = useTheme();

  if (loading || !available) return null;

  const isOnline = status === GATEWAY_STATUS.ONLINE;
  const isOffline = status === GATEWAY_STATUS.OFFLINE;

  const dotColor = isOnline
    ? (theme.palette.status?.normalGreen || theme.palette.success.main)
    : isOffline
      ? (theme.palette.status?.alarmRed || theme.palette.error.main)
      : theme.palette.grey[500];

  const label = isOnline ? 'Online' : isOffline ? 'Offline' : 'Unknown';
  const heartbeatText =
    status !== GATEWAY_STATUS.UNKNOWN
      ? formatHeartbeatAge(secondsSinceHeartbeat)
      : null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        mt: 0.75,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0,
            boxShadow: `0 0 0 2px ${dotColor}33`,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: dotColor,
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            lineHeight: 1.2,
          }}
        >
          Router Status: {label}
        </Typography>
      </Box>
      {heartbeatText && (
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            lineHeight: 1.2,
          }}
        >
          Last ping: {heartbeatText}
        </Typography>
      )}
    </Box>
  );
};

export default GatewayStatusBanner;
